import React from 'react';

import Button from '@mui/material/Button';
import { styled } from '@mui/material/styles';

import { type StyledComponent } from '@emotion/styled';

import type { IButtonVariantTheme } from '../../styles';
import type { IButtonProps } from './Button.types';

import { ButtonBlade } from './ButtonBlade';

const TRANSITION_TIME = '125ms';

export type ButtonStyledComponent = StyledComponent<IButtonProps>;

export const StyledButton: ButtonStyledComponent = styled(
  ({
    variant = 'primary',
    disabled,
    fullWidth,
    href,
    className,
    children,
    loading,
    onClick,
    ...rest
  }: IButtonProps) => (
    <Button
      disableRipple={true}
      className={className}
      disabled={disabled}
      fullWidth={fullWidth}
      href={href}
      onClick={onClick}
      disableElevation={true}
      {...rest}
    >
      <div className="button-content">
        {loading ? 'Loading…' : children}
        <ButtonBlade />
      </div>
    </Button>
  ),
  {
    shouldForwardProp: (prop) => prop !== 'maxWidth'
  }
)(({ theme, ...props }) => {
  const { maxWidth, variant = 'primary', size } = props;

  const { base, hover, active, disabled } = theme.elements.button[variant]
    .dark as IButtonVariantTheme;

  const isCardVariant = variant === 'card';

  return {
    position: 'relative',
    outline: 'none',
    cursor: 'pointer',
    maxWidth: maxWidth ?? 'auto',
    overflow: 'hidden',

    padding: 0,
    margin: 0,

    transition: `all ${TRANSITION_TIME} ease-out`,

    backgroundColor: base.backgroundColor,
    border: 'none',
    borderRadius: isCardVariant ? 0 : base.borderRadius,
    color: base.textColor,

    '--button-blade-color': base.bladeColor,

    '.button-content': {
      position: 'relative',
      display: 'flex',
      alignItems: 'center',

      gap: theme.spacing(0),
      padding:
        size === 'small' ? `0 ${theme.spacing(1)}` : `${theme.spacing(0)} ${theme.spacing(1)}`
    },

    '&::before': {
      content: '""',
      position: 'absolute',
      inset: 0,
      backgroundColor: 'transparent',
      border: base.border,
      borderRadius: isCardVariant ? 0 : base.borderRadius,
      transition: `all ${TRANSITION_TIME} ease-out`,
      zIndex: 100
    },

    ':hover': {
      backgroundColor: hover.backgroundColor,
      borderRadius: isCardVariant ? 0 : hover.borderRadius,
      color: hover.textColor,

      '--button-blade-color': hover.bladeColor,
      '--button-blade-scale': '1rem', // TODO: This may need to change based on usage

      '&::before': {
        border: hover.border
      }
    },

    ':active': {
      opacity: 0.7,
      backgroundColor: active.backgroundColor,
      borderRadius: isCardVariant ? 0 : active.borderRadius,
      color: active.textColor,

      '--button-blade-color': active.bladeColor,

      '&::before': {
        border: active.border
      },

      '.button-content': {
        color: active.textColor
      }
    },

    ':disabled': {
      cursor: 'default',
      backgroundColor: isCardVariant ? undefined : disabled.backgroundColor,
      borderRadius: isCardVariant ? 0 : disabled.borderRadius,
      color: disabled.textColor,

      '--button-blade-color': disabled.bladeColor,

      ':active, :focus': {
        opacity: 1
      },

      '&::before': {
        border: disabled.border
      },

      '.button-content': {
        color: disabled.textColor
      }
    }
  };
});
